import { createTheme, Theme } from '@mui/material/styles'
import { THEME_PRESETS } from './presets'

// Paletas de cores disponíveis (Premium e Sóbrias)
export type PaletteOption = 'default' | 'skyline' | 'emerald' | 'slate' | 'classic' | 'red'

interface PaletteColors {
  primary: string
  secondary: string
  accent?: string
}

const palettes: Record<PaletteOption, PaletteColors> = {
  default: {
    primary: '#2563eb', // Azul Galatea Moderno
    secondary: '#64748b', // Slate 500
    accent: '#3b82f6',
  },
  skyline: {
    primary: '#0284c7', // Sky 600
    secondary: '#0ea5e9',
    accent: '#0369a1',
  },
  emerald: {
    primary: '#059669', // Emerald 600
    secondary: '#10b981',
    accent: '#047857',
  },
  slate: {
    primary: '#334155', // Slate 700
    secondary: '#64748b',
    accent: '#1e293b',
  },
  classic: {
    primary: '#1e3a8a', // Blue 900 (Forte/Profissional)
    secondary: '#3b82f6',
    accent: '#172554',
  },
  red: {
    primary: '#dc2626', // Red 600
    secondary: '#ef4444',
    accent: '#991b1b',
  },
}

export const createCustomTheme = (mode: 'light' | 'dark', paletteOption: PaletteOption = 'default'): Theme => {
  const colors = palettes[paletteOption]

  const isLight = mode === 'light'
  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: isLight ? colors.secondary : colors.primary,
        dark: !isLight ? colors.primary : colors.accent || colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: isLight ? '#f8fafc' : '#0f172a', // Slate 50 vs Slate 900
        paper: isLight ? '#ffffff' : '#1e293b', // White vs Slate 800
      },
      text: {
        primary: isLight ? '#0f172a' : '#e2e8f0',   // Slate 900 vs Slate 200
        secondary: isLight ? '#64748b' : '#94a3b8', // Slate 500 vs Slate 400
      },
      success: {
        main: isLight ? '#059669' : '#10b981', // Emerald 600 / 500
      },
      warning: {
        main: isLight ? '#d97706' : '#f59e0b', // Amber 600 / 500
      },
      error: {
        main: isLight ? '#dc2626' : '#ef4444', // Red 600 / 500
      },
      info: {
        main: isLight ? '#2563eb' : '#3b82f6', // Blue 600 / 500
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        'Outfit',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      htmlFontSize: 14,
      fontSize: 13,
      body1: {
        fontSize: '0.95rem',
      },
      body2: {
        fontSize: '0.85rem',
      },
      button: {
        fontSize: '0.9rem',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
          outlined: {
            color: isLight ? undefined : '#E2E8F0',
            borderColor: isLight ? undefined : '#E2E8F0',
            '&:hover': {
              borderColor: isLight ? undefined : '#F1F5F9',
              backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'background-color 120ms, border-color 120ms',
            backgroundColor: isLight ? undefined : 'rgba(255,255,255,0.03)',
            '&:hover': {
              backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isLight ? undefined : '#E2E8F0',
            '&:hover': {
              backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
      // Padrão de altura/espaço uniforme em todas as grids/tabelas
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: 14,
            paddingBottom: 14,
            paddingLeft: 16,
            paddingRight: 16,
          },
          head: {
            paddingTop: 14,
            paddingBottom: 14,
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
          },
        },
      },
      // Barra de labels (Tabs): texto centralizado na altura
      MuiTab: {
        styleOverrides: {
          root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          },
        },
      },
      // Campos de pesquisa/filtro: altura ainda mais reduzida (size="small")
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.MuiInputBase-sizeSmall': {
              minHeight: 28,
              '& .MuiInputBase-input': {
                paddingTop: 4,
                paddingBottom: 4,
              },
            },
          },
          input: {
            '&.MuiInputBase-inputSizeSmall': {
              paddingTop: 4,
              paddingBottom: 4,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.MuiInputBase-sizeSmall': {
              minHeight: 28,
              '& .MuiOutlinedInput-input': {
                paddingTop: 4,
                paddingBottom: 4,
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.MuiInputLabel-sizeSmall': {
              fontSize: '0.8125rem',
            },
          },
        },
      },
    },
  })
}

// Temas pré-definidos para compatibilidade
export const lightTheme = createCustomTheme('light', 'default')
export const darkTheme = createCustomTheme('dark', 'default')

// Tema padrão (light)
export const theme: Theme = lightTheme

// Exportar paletas para uso em componentes
export { palettes }

export const createThemeFromPreset = (mode: 'light' | 'dark', presetId: string): Theme => {
  const preset = THEME_PRESETS.find(p => p.id === presetId)
  const primaryColor = preset?.primary || palettes.default.primary
  const isLight = mode === 'light'
  const isGalatea = presetId === 'galatea-navy'
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: '#64748b',
      },
      background: {
        default: isGalatea && isLight ? '#E7F0FF' : (isLight ? '#f8fafc' : '#0f172a'),
        paper: isLight ? '#ffffff' : '#1e293b',
      },
      text: {
        primary: isLight ? '#0f172a' : '#e2e8f0',
        secondary: isLight ? '#64748b' : '#94a3b8',
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        'Outfit',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      htmlFontSize: 14,
      fontSize: 13,
      button: { fontSize: '0.9rem' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
          outlined: {
            color: isLight ? undefined : '#E2E8F0',
            borderColor: isLight ? undefined : '#E2E8F0',
            '&:hover': {
              borderColor: isLight ? undefined : '#F1F5F9',
              backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'background-color 120ms, border-color 120ms',
            backgroundColor: isLight ? undefined : 'rgba(255,255,255,0.03)',
            '&:hover': {
              backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isLight ? undefined : '#E2E8F0',
            '&:hover': {
              backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
            },
          },
        },
      },
    },
  })
}
